import React from 'react';

import { Badge, Card, Group, Stack, Text, Title } from '@mantine/core';

import { Button } from '@gtintel/signin/components/common/Button';
import { AuthLayout } from '@gtintel/signin/components/layout/AuthLayout';
import { useAuth } from '@gtintel/signin/hooks/useAuth';

interface AppSelectionCardProps {
  title: string;
  badge: 'NEW' | 'LEGACY' | 'DEV';
  description: string;
  onClick: () => void;
}

const AppSelectionCard: React.FC<AppSelectionCardProps> = ({
  title,
  badge,
  description,
  onClick,
}) => {
  let badgeText;
  let badgeColor;
  switch (badge) {
    case 'NEW':
      badgeText = 'NEW';
      badgeColor = 'yellow.6';
      break;
    case 'LEGACY':
      badgeText = 'LEGACY';
      badgeColor = 'navy.9';
      break;
    case 'DEV':
      badgeText = 'DEV';
      badgeColor = 'orange.6';
      break;
    default:
      badgeText = '';
  }

  return (
    <Card withBorder shadow="lg" radius="md" p="lg" bd="2px solid var(--mantine-color-navy-9)">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={3}>{title}</Title>
          <Badge size="sm" variant="filled" color={badgeColor}>
            {badgeText}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed">
          {description}
        </Text>
        <Button onClick={() => onClick()} fullWidth size="lg" variant="primary">
          Go to {title}
        </Button>
      </Stack>
    </Card>
  );
};

export const RedirectSelection: React.FC = () => {
  const { redirectUrls, userEmail } = useAuth();

  // Use URLs from context or fall back to defaults
  const urls = redirectUrls || {
    nexus: 'http://localhost:5173',
    platform: 'http://localhost:4200',
  };

  // Optional destination, only rendered when configured for the environment
  const hubUrl = import.meta.env.VITE_HUB_URL;

  const handleRedirect = (url: string) => {
    let finalUrl = url;

    // For platform URL, append email parameter for auth callback
    if (url === urls.platform && userEmail) {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = `${url}/${separator}email=${encodeURIComponent(userEmail)}`;
    }

    window.location.href = finalUrl;
  };

  return (
    <AuthLayout
      title="Choose Your Destination"
      subtitle="You've been successfully authenticated. Choose where you'd like to go:"
    >
      <Group gap="md" mt="xl" justify="center" align="center">
        <Stack gap="md" mb="md" w="50%" justify="center" align="center">
          <AppSelectionCard
            title="vNext"
            badge="NEW"
            description="Our next-generation platform with enhanced screening capabilities and modern interface."
            onClick={() => handleRedirect(urls.nexus)}
          />
          {hubUrl && (
            <AppSelectionCard
              title="GTI Hub"
              badge="NEW"
              description="The new central hub for accessing GTI applications and services."
              onClick={() => handleRedirect(hubUrl)}
            />
          )}
          <AppSelectionCard
            title="GTI Platform"
            badge="LEGACY"
            description="Access the traditional GTI platform with full feature set and familiar interface."
            onClick={() => handleRedirect(urls.platform)}
          />
          {process.env.NODE_ENV === 'development' &&
            import.meta.env.VITE_MORE_REDIRECTS?.split(',').map((url: string) => (
              <AppSelectionCard
                title={url.split('_')[0]}
                badge="DEV"
                description="Access a development environment."
                onClick={() => handleRedirect(url.split('_')[1])}
              />
            ))}
          <Text size="xs" c="dimmed">
            You can always switch between platforms using the navigation menu.
          </Text>
        </Stack>
      </Group>
    </AuthLayout>
  );
};
